import { AttributeType, generateId, GeometryType } from "@evergis/api";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DATE_OF_IMAGE_ATTRIBUTE, NAME_ATTRIBUTE, STATUS_ATTRIBUTE } from "../../../evergis/constants";
import { EditGeometryType, geometryTypes } from "../../../evergis/core/attributes/geometryTypes";
import { EditResource } from "../SelectFiles/Edit/EditResource";
import { useNewLayerContext } from "./newLayerContext";
import { NewLayerWizardStep } from "./NewLayerWizardStep";
import { GeometryTypeContainer, GeometryTypeIcon, GeometryTypes, GeometryTypeTitle } from "./styled";

// eslint-disable-next-line max-lines-per-function
export const NewLayerResource: FC = () => {
  const { t } = useTranslation("common");
  const {
    path,
    setPath,
    name,
    setName,
    description,
    setDescription,
    tags,
    setTags,
    systemName,
    setSystemName,
    item,
    geometryType,
    setGeometryType,
    setAttributes,
  } = useNewLayerContext();

  const newLayerGeometryTypes = useMemo(
    () =>
      geometryTypes
        .filter(({ value }) => value !== GeometryType.Multipoint)
        .map(value => ({
          ...value,
          text: value.langKey ? t(value.langKey, { ns: "common" }) : value.text,
        })),
    [t],
  );

  const defaultCommonAttributes = useMemo(
    () => [
      {
        id: generateId(),
        attributeName: "Attribute1",
        alias: "",
        columnName: "Attribute1",
        type: AttributeType.String,
        isDisplayed: true,
        isEditable: true,
      },
    ],
    [],
  );

  const defaultRasterAttributes = useMemo(
    () => [
      {
        id: generateId(),
        attributeName: NAME_ATTRIBUTE,
        columnName: NAME_ATTRIBUTE,
        alias: t("text"),
        type: AttributeType.String,
        expression: null,
        isDisplayed: true,
        isEditable: true,
      },
      {
        id: generateId(),
        attributeName: DATE_OF_IMAGE_ATTRIBUTE,
        columnName: DATE_OF_IMAGE_ATTRIBUTE,
        alias: t("dateTime"),
        type: AttributeType.DateTime,
        expression: null,
        isDisplayed: true,
        isEditable: true,
      },
      {
        id: generateId(),
        attributeName: STATUS_ATTRIBUTE,
        columnName: STATUS_ATTRIBUTE,
        alias: t("text"),
        type: AttributeType.String,
        expression: null,
        isDisplayed: true,
        isEditable: true,
      },
    ],
    [t],
  );

  const updateGeometryType = useCallback(
    (type: GeometryType | EditGeometryType, typeText: string) => {
      const isDefaultOrEmptyAlias = !name || newLayerGeometryTypes.find(({ text }) => text === name);

      if (isDefaultOrEmptyAlias) {
        setName(typeText);
      }
      setGeometryType(type as GeometryType);

      if (type === EditGeometryType.Raster) {
        setAttributes(defaultRasterAttributes);
      } else {
        setAttributes(defaultCommonAttributes);
      }
    },
    [
      setGeometryType,
      setName,
      name,
      newLayerGeometryTypes,
      setAttributes,
      defaultRasterAttributes,
      defaultCommonAttributes,
    ],
  );

  return (
    <NewLayerWizardStep>
      <GeometryTypes>
        {newLayerGeometryTypes.map(({ text, icon, value }) => (
          <GeometryTypeContainer
            key={value}
            selected={geometryType === value}
            onClick={() => updateGeometryType(value as GeometryType, text)}
          >
            <GeometryTypeIcon primary={geometryType === value} kind={icon} />
            <GeometryTypeTitle>{text}</GeometryTypeTitle>
          </GeometryTypeContainer>
        ))}
      </GeometryTypes>
      <EditResource
        item={item}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        tags={tags}
        setTags={setTags}
        systemName={systemName}
        setSystemName={setSystemName}
        withSystemName
        withPath
        path={path}
        setPath={setPath}
      />
    </NewLayerWizardStep>
  );
};
